/**
 * Voice Page Tour — one-time coach-mark walkthrough for /dashboard/voice.
 *
 * Auto-opens on the first visit (localStorage key "buzzerai-voice-tour-v1").
 * A persistent "Take a tour" button in the page header replays it on demand.
 * Skip / Next / Back / Got it navigation. Progress dots at bottom.
 */
import { useEffect, useState } from "react";
import { X, ArrowLeft, ArrowRight, Sparkle, CheckCircle } from "@phosphor-icons/react";

const TOUR_KEY = "buzzerai-voice-tour-v1";

export const hasSeenVoiceTour = () => {
  try { return localStorage.getItem(TOUR_KEY) === "1"; }
  catch (e) { /* Safari private mode blocks localStorage access — treat as seen */ return true; }
};

export const markVoiceTourSeen = () => {
  try { localStorage.setItem(TOUR_KEY, "1"); }
  catch (e) { /* Safari private mode blocks localStorage.setItem — silently ignore */ console.debug("voice tour mark failed:", e?.message); }
};

const STEPS = [
  {
    title: "Welcome to the Voice page",
    body: "In about 3 minutes you'll have a real phone number ringing into BuzzerAI. This quick tour shows you what each section does.",
    emoji: "sparkle",
  },
  {
    title: "1 · Who answers your phone",
    body: "Pick AI Receptionist (BuzzerAI answers 24/7) or Live Staff (calls forward to a real person). You can flip this any time — the switch takes effect on the very next call.",
    highlight: "handler-ai",
  },
  {
    title: "2 · Forward your existing number",
    body: "Enter your current business phone in the detector below. We'll show the exact forwarding code for your carrier (like *72) and the BuzzerAI answering line you point it at. Your guests keep dialing the number they know.",
    highlight: "voice-forwarding-helper",
  },
  {
    title: "3 · Connect the AI answering line",
    body: "Paste your Twilio Account SID + Auth Token, pick a number, hit Save. We auto-wire the webhooks — no clicking around the Twilio console. This becomes the number your existing line forwards to.",
    highlight: "twilio-sid",
  },
  {
    title: "4 · Test + go live",
    body: "Send a test SMS to your own phone. If you see 'mocked=true', creds aren't saved yet. Once real messages arrive, every incoming call shows up in the call log on the right — with full transcript.",
    highlight: null,
  },
  {
    title: "You're ready!",
    body: "Any time you want to see this tour again, click 'Take a tour' at the top-right of the Voice page. Questions? Hit the Support button in the bottom corner.",
    emoji: "check",
  },
];

export default function VoiceTour({ open, onClose }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  if (!open) return null;

  const s = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  const close = () => {
    markVoiceTourSeen();
    onClose();
  };

  const next = () => {
    if (isLast) return close();
    setStep(step + 1);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#121914]/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="voice-tour"
      onClick={close}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl border border-[#E1DFD5] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#E1DFD5] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1E3A2F] flex items-center justify-center">
            {s.emoji === "check" ? (
              <CheckCircle size={20} color="#fff" weight="fill" />
            ) : (
              <Sparkle size={20} color="#fff" weight="fill" />
            )}
          </div>
          <div className="flex-1">
            <div className="overline text-[#5C6B61]">Voice tour · {step + 1} of {STEPS.length}</div>
            <div className="font-heading font-extrabold text-lg leading-tight" data-testid="voice-tour-title">{s.title}</div>
          </div>
          <button
            type="button"
            onClick={close}
            className="w-9 h-9 rounded-full hover:bg-[#F1D7D2] flex items-center justify-center"
            data-testid="voice-tour-close"
            aria-label="Skip tour"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          <p className="text-sm text-[#121914] leading-relaxed" data-testid="voice-tour-body">{s.body}</p>
        </div>

        <div className="px-5 pb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5" data-testid="voice-tour-progress">
            {STEPS.map((s, i) => (
              <span
                key={s.title}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? "bg-[#1E3A2F] w-4" : i < step ? "bg-[#1E3A2F]/50" : "bg-[#E1DFD5]"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="btn-ghost !py-2 !px-3 text-sm inline-flex items-center gap-1"
                data-testid="voice-tour-back"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1"
              data-testid="voice-tour-next"
            >
              {isLast ? "Got it" : "Next"} {!isLast && <ArrowRight size={14} color="#fff" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
